import * as actionTypes from './actionTypes';
import loginService from 'services/loginService';
import userService from 'services/userService';
import landingService from 'services/landingService';
import voucherService from 'services/voucherService';
import objectUtils from 'utils/object';
import googleAnalyticsUtils from 'utils/googleAnalytics';
import inspectletUtils from 'utils/inspectlet';
import queryString from 'utils/queryString';
import { ERROR_CODES, knownError, serverIdOf, userError } from 'utils/userError';
import { fetchGuestPolicy } from './Cart';

export const toggleRegisterCompany = toggle => {
  return {
    type: actionTypes.TOGGLE_REGISTER_COMPANY,
    toggle: toggle,
  };
};

const loggingIn = () => {
  return {
    type: actionTypes.USER_LOGGING_IN,
  };
};

function loggingOut() {
  return {
    type: actionTypes.USER_LOGGING_OUT,
  };
}

export const sendEmailToRegisterCompany = payload => {
  return dispatch => {
    landingService
      .sendEmailToRegisterCompany(payload)
      .then(data => {
        dispatch(successSendEmailToRegisterCompany());
      })
      .catch(err => {
        console.log(err);
      });
  };
};

const successSendEmailToRegisterCompany = () => {
  return {
    type: actionTypes.SUCCESS_SEND_EMAIL_REGISTER_COMPANY,
  };
};

export const sendEmailContact = payload => {
  return dispatch => {
    dispatch(toggleSendEmailContact(false));
    landingService
      .sendEmailContact(payload)
      .then(data => {
        dispatch(toggleSendEmailContact(true));
      })
      .catch(err => {
        console.log(err);
      });
  };
};

const toggleSendEmailContact = toggle => {
  return {
    type: actionTypes.TOGGLE_SEND_EMAIL_CONTANT,
    toggle: toggle,
  };
};

export const PRE_LOGIN_ROUTES = {
  PASSWORD: 'password',
  SIGNUP: 'signup',
  NO_OFFICE: 'no_office',
  RETAIL_ONLY: 'retail_only',
};

// Where the email step goes, from the POST /user/v1/login/pre-login answer.
// A registered account always goes to the password/sign-in step. Signup needs an
// explicit userAlreadyRegistered === false, so a partial or odd answer can never send
// an existing account to the Terms & Conditions page.
export const preLoginRoute = data => {
  const answer = data || {};
  if (answer.userAlreadyRegistered === true) {
    return answer.retailOnly ? PRE_LOGIN_ROUTES.RETAIL_ONLY : PRE_LOGIN_ROUTES.PASSWORD;
  }
  if (!answer.hasRelatedBusiness) {
    return PRE_LOGIN_ROUTES.NO_OFFICE;
  }
  if (answer.retailOnly) {
    return PRE_LOGIN_ROUTES.RETAIL_ONLY;
  }
  if (answer.userAlreadyRegistered === false) {
    return PRE_LOGIN_ROUTES.SIGNUP;
  }
  return PRE_LOGIN_ROUTES.NO_OFFICE;
};

// The email belongs to a retail account (personal ordering), not an office account.
export const RETAIL_ONLY_MESSAGE =
  'This email is set up for personal ordering, not an office account. Sign in with the work email your company ' +
  'registered with us, or contact support@lunch.team and we will sort it out.';

// No company we deliver to matches this email's domain yet.
export const OFFICE_NOT_SET_UP_TITLE = "We don't deliver to your office yet";
export const OFFICE_NOT_SET_UP_GUIDANCE =
  "Register your company and we'll be in touch, or chat with us using the chat button at the bottom of the page.";
export const OFFICE_NOT_SET_UP_MESSAGE = `${OFFICE_NOT_SET_UP_TITLE}. ${OFFICE_NOT_SET_UP_GUIDANCE}`;

// Only the latest email check may change the page (two quick "Next" clicks, or a slow
// answer for an older email, must not win).
let latestPreLoginRequest = 0;

export const requestVerifyEmail = credentials => {
  return dispatch => {
    const requestId = ++latestPreLoginRequest;
    // Clear any signup started earlier in this tab so it cannot fire again.
    dispatch({ type: actionTypes.CLOSE_SIGNUP });
    return loginService
      .preLogin(credentials, { showSpinner: false })
      .then(data => {
        if (requestId !== latestPreLoginRequest) {
          return undefined;
        }
        const route = preLoginRoute(data);
        if (route === PRE_LOGIN_ROUTES.PASSWORD) {
          dispatch(preLogin(credentials.email));
        } else if (route === PRE_LOGIN_ROUTES.SIGNUP) {
          dispatch(startSignup(credentials.email));
        } else if (route === PRE_LOGIN_ROUTES.RETAIL_ONLY) {
          dispatch(
            requestFailed(
              knownError(ERROR_CODES.ACCOUNT_RETAIL_ONLY, {
                title: 'This email is for personal ordering',
                message: RETAIL_ONLY_MESSAGE,
              })
            )
          );
        } else {
          dispatch(
            requestFailed(
              knownError(ERROR_CODES.ACCOUNT_COMPANY_NOT_SERVED, {
                title: OFFICE_NOT_SET_UP_TITLE,
                guidance: OFFICE_NOT_SET_UP_GUIDANCE,
              })
            )
          );
        }
        return route;
      })
      .catch(error => {
        if (requestId !== latestPreLoginRequest) {
          return undefined;
        }
        if (error && !!error.errors) {
          dispatch(
            requestFailed(
              userError(error, {
                area: 'ACCOUNT',
                code: ERROR_CODES.ACCOUNT_EMAIL_INVALID,
                title: 'Check your email address',
                guidance: 'Enter a work email like name@company.ie.',
              })
            )
          );
        } else {
          dispatch(requestFailed(userError(error, { area: 'AUTH', task: 'check your email' })));
        }
        return undefined;
      });
  };
};

// "Use a different email" on the sign-in step.
export const resetPreLogin = () => {
  return {
    type: actionTypes.RESET_PRE_LOGIN,
  };
};

// options.showSpinner=false: the sign-in panel shows its own progress, and the global
// spinner would bounce the landing page to /Menu and back while the request runs.
// Returns the request promise (it never rejects; failures show the error modal).
export const requestLogin = (credentials, options) => {
  return dispatch => {
    const params = queryString.parse(window.location.search);
    return loginService
      .login(credentials, options)
      .then(userDetails => {
        if (process.env.NODE_ENV === 'development') {
          localStorage.setItem(
            '@LT/DEV_KEYS',
            window.btoa(`${credentials.email}:${credentials.password}`)
          );
        }
        googleAnalyticsUtils.sendWhoIsAccessing(userDetails.username);
        dispatch(requestSuccessLogin(userDetails));
        dispatch(fetchGuestPolicy());
        inspectletUtils.tagSession(userDetails);
        dispatch(createLinkVoucher(params));
        localStorage.setItem('country', userDetails.country);
      })
      .catch(err => {
        if (err && err.response && err.response.status === 401) {
          // Neutral: never says whether the account exists.
          dispatch(
            requestFailed(
              userError(null, {
                area: 'AUTH',
                code: ERROR_CODES.AUTH_WRONG_PASSWORD,
                title: "We couldn't sign you in",
                message: "The email or password isn't right.",
                guidance: 'Check them and try again, or reset your password.',
                serverId: serverIdOf(err),
              })
            )
          );
        } else {
          dispatch(requestFailed(userError(err, { area: 'AUTH', task: 'sign you in' })));
        }
      });
  };
};

// After a successful email sign-in link (verify-magic-link). The response has the same
// shape as POST /user/v1/login, and the session cookies are already set by the browser.
export const completeMagicLinkLogin = userDetails => {
  return dispatch => {
    const params = queryString.parse(window.location.search);
    googleAnalyticsUtils.sendWhoIsAccessing(userDetails.username);
    dispatch(requestSuccessLogin(userDetails));
    inspectletUtils.tagSession(userDetails);
    dispatch(createLinkVoucher(params));
    if (userDetails.country) {
      localStorage.setItem('country', userDetails.country);
    }
  };
};

export const requestEmailResetPassword = email => {
  return dispatch => {
    userService
      .requestEmailResetPassword({ email: email })
      .then(data => {
        dispatch(requestSentEmailResetPasswordSuccess());
      })
      .catch(err => {
        console.log(err);
      });
  };
};

const requestSentEmailResetPasswordSuccess = email => {
  return {
    type: actionTypes.REQUEST_SENT_EMAIL_RESET_PASSWORD_SUCCESS,
  };
};

export function tryAutomaticallyLogin() {
  return async dispatch => {
    const params = queryString.parse(window.location.search);
    dispatch(loggingIn());

    try {
      const response = await loginService.login();
      if (!!objectUtils.get(response, 'idUser', undefined)) {
        dispatch(requestSuccessLogin(response));
        dispatch(fetchGuestPolicy());
        inspectletUtils.tagSession(response);
        dispatch(createLinkVoucher(params));
      } else {
        dispatch(loggingOut());
      }
    } catch (error) {
      dispatch(loggingOut());
    }
  };
}

const startSignup = email => {
  return {
    type: actionTypes.START_SIGNUP,
    email: email,
  };
};

// errorMessage: a userError (src/utils/userError.js), shown by ModalError.
const requestFailed = error => {
  return {
    type: actionTypes.REQUEST_FAILED,
    errorMessage: error,
  };
};

const preLogin = email => {
  return {
    type: actionTypes.PRE_LOGIN,
    email: email,
  };
};

const requestSuccessLogin = userDetails => {
  return {
    type: actionTypes.REQUEST_SUCCESS_LOGIN,
    userDetails: userDetails,
  };
};

export const closeModalError = () => {
  return {
    type: actionTypes.CLOSE_MODAL_ERROR,
  };
};

export const openModalError = messageError => {
  return {
    type: actionTypes.CHECKOUT_REALEX_TOGGLE_SHOW_MODAL_ERROR,
    payload: { show: true, messageError: messageError },
  };
};

export const createLinkVoucher = params => {
  return async dispatch => {
    if (params.isCodeLink) {
      try {
        voucherService
          .createLinkVoucher(params.code)
          .then(data => {
            dispatch(
              requestSuccessCreateVoucher({ message: `You've got a voucher!` })
            );
          })
          .catch(err => {
            dispatch(requestFailedCreateVoucher({ message: voucherLinkError(err) }));
          });
      } catch (error) {
        dispatch(requestFailedCreateVoucher({ message: voucherLinkError(error) }));
      }
    }
  };
};

const voucherLinkError = error =>
  userError(error, {
    area: 'VOUCHER',
    code: ERROR_CODES.VOUCHER_INVALID,
    title: "We couldn't add this voucher",
    message: 'This voucher link has expired or has already been used.',
    guidance: 'Ask the person who sent it for a new link.',
  });

const requestSuccessCreateVoucher = successMessage => {
  return {
    type: actionTypes.REQUEST_SUCCESS_CREATE_VOUCHER,
    message: successMessage.message,
  };
};

const requestFailedCreateVoucher = errorMessage => {
  return {
    type: actionTypes.REQUEST_FAILED_CREATE_VOUCHER,
    message: errorMessage.message,
  };
};

export const closeSuccessModalCreateLinkVoucher = successMessage => {
  return {
    type: actionTypes.CLOSE_MODAL_SUCCESS_CREATE_LINK_VOUCHER,
  };
};

export const closeErrorModalCreateLinkVoucher = () => {
  return {
    type: actionTypes.CLOSE_MODAL_ERROR_CREATE_LINK_VOUCHER,
  };
};



// WEBPACK FOOTER //
// ./src/store/actions/Landing.js